import moment from 'moment'

import {
  fromTimestamp,
  getDefaultDate,
  getDefaultRange,
  toTimestamp} from 'components/DateRangePicker'
import {
  FilterableEntity,
  FilterConfigAndAvailableValues,
  FilterGroup,
  FilterGroupsByPage,
  FilterOperator,
  FilterValues} from 'context/filtersContext'

const isKnownType = (filter: FilterValues) => {
  return filter.filterType in FilterableEntity
}

export const defaultFilterTypes = [
  FilterableEntity.ACCOUNT_OWNERS,
  FilterableEntity.TARGETED,
  FilterableEntity.ACTIVITIES,
  FilterableEntity.CONTACT_TYPES,
  FilterableEntity.CONTACT_ROLE,
  FilterableEntity.PAGE_URL,
  FilterableEntity.CUSTOM_ATTRIBUTE
]

export const deserializeDateRange = (dateRangeJson: string) => {
  const { startDate, endDate, type } = JSON.parse(dateRangeJson)
  return { startDate: moment(startDate), endDate: moment(endDate), type }
}

export const normalize = (filter: FilterValues) => {
  const { filterType, filterSubtype, operator, attributeType } = filter
  let normValues = filter.values

  if (filterType === 'DATE_RANGE' || attributeType === 'DATE' || attributeType === 'DATETIME') {
    const { values } = filter

    if (moment.isMoment(values)) {
      normValues = values.valueOf()
    } else {
      normValues = toTimestamp(values)
    }
  }

  if (Array.isArray(normValues)) {
    normValues = normValues.map(({ label, value }) => ({
      label,
      value
    }))
  }

  return {
    filterType,
    ...(filterSubtype ? { filterSubtype } : {}),
    ...(attributeType ? { attributeType } : {}),
    operator,
    values: normValues
  }
}

export const serializeFilter = (filterGroups: FilterGroup[]): any => {
  const normalizedGroups = filterGroups.map((filters: FilterValues[]) =>
    filters.filter(isKnownType).map(normalize)
  )

  return normalizedGroups
}

export const serializeFilters = (filterGroupsByPage: FilterGroupsByPage): string => {
  const normalized = {} as any

  for (const [page, filterGroups] of Object.entries(filterGroupsByPage)) {
    normalized[page] = filterGroups.map((filters: FilterValues[]) =>
      filters.filter(isKnownType).map(normalize)
    )
  }

  return JSON.stringify({
    version: 2,
    filterGroupsByPage: normalized
  })
}

export const deserializeFilters = (json: string): FilterGroupsByPage | undefined => {
  const reviver = (key: string | symbol, value: any) => {
    // WORKAROUND: Replace the saved date with the default range (the last two
    // weeks). Users expect to see their most recent activity by default so
    // saving a fixed range was causing confusion.
    if (value?.filterType === FilterableEntity.DATE_RANGE) {
      return {
        ...value,
        operator: FilterOperator.BETWEEN,
        values: getDefaultRange()
      }
    }

    if (value?.attributeType === 'DATE' || value?.attributeType === 'DATETIME') {
      let normValues = value.values

      if (typeof value.values === 'object') {
        normValues = fromTimestamp(value.values)
      } else if (typeof value.values === 'number') {
        normValues = moment(value.values)
      }

      return {
        ...value,
        values: normValues
      }
    }

    // WORKAROUND: This label/value pair was mismatched. Repair the filter if
    // it was saved that way.
    if (
      value?.filterType === FilterableEntity.TARGETED &&
      value?.values?.label === 'Targeted' &&
      value?.values?.value === 'ALL'
    ) {
      return {
        ...value,
        values: { label: 'Targeted', value: 'TARGETED' }
      }
    }

    return value
  }

  const parsed = JSON.parse(json, reviver)

  if (parsed.version === 2) {
    return removeDeprecatedFilters(parsed.filterGroupsByPage)
  }

  if (parsed.version === 1) {
    return convertV1ToV2(parsed)
  }

  window.console.warn(`Filters version ${parsed.version} not supported.`)

  return undefined
}

const removeDeprecatedFilters = (filterGroupsByPage: FilterGroupsByPage): FilterGroupsByPage => {
  const filterDateRangesFromGroup = (group: FilterValues[]) => {
    return group.filter(
      ({ filterType }: FilterValues) => filterType !== FilterableEntity.DATE_RANGE
    )
  }

  filterGroupsByPage.ACTIVITIES = filterGroupsByPage.ACTIVITIES.map(filterDateRangesFromGroup)
  filterGroupsByPage.ACCOUNT_DETAILS = filterGroupsByPage.ACCOUNT_DETAILS.map(
    filterDateRangesFromGroup
  )

  return filterGroupsByPage
}

function convertV1ToV2(parsed: { filterGroups: FilterValues[][] }) {
  const firstGroup = parsed.filterGroups[0]

  const supportedFilters = {
    ACCOUNTS: [
      FilterableEntity.ACCOUNT_OWNERS,
      FilterableEntity.TARGETED,
      FilterableEntity.ACTIVITIES,
      FilterableEntity.CONTACT_TYPES,
      FilterableEntity.CONTACT_ROLE,
      FilterableEntity.PAGE_URL,
      FilterableEntity.CUSTOM_ATTRIBUTE
    ],
    ACTIVITIES: [
      FilterableEntity.ACCOUNT_OWNERS,
      FilterableEntity.TARGETED,
      FilterableEntity.ACTIVITIES,
      FilterableEntity.CONTACT_TYPES,
      FilterableEntity.CONTACT_ROLE,
      FilterableEntity.PAGE_URL,
      FilterableEntity.CUSTOM_ATTRIBUTE
    ],

    ACCOUNT_DETAILS: [
      FilterableEntity.ACTIVITIES,
      FilterableEntity.CONTACT_TYPES,
      FilterableEntity.CONTACT_ROLE,
      FilterableEntity.PAGE_URL
    ],

    CONTACT_DETAILS: [FilterableEntity.ACTIVITIES, FilterableEntity.PAGE_URL]
  }

  return {
    ACCOUNTS: [
      firstGroup.filter(({ filterType }: FilterValues) =>
        supportedFilters.ACCOUNTS.includes(filterType)
      )
    ],
    ACTIVITIES: [
      firstGroup.filter(({ filterType }: FilterValues) =>
        supportedFilters.ACTIVITIES.includes(filterType)
      )
    ],
    ACCOUNT_DETAILS: [
      firstGroup.filter(({ filterType }: FilterValues) =>
        supportedFilters.ACCOUNT_DETAILS.includes(filterType)
      )
    ],
    CONTACT_DETAILS: [
      firstGroup.filter(({ filterType }: FilterValues) =>
        supportedFilters.CONTACT_DETAILS.includes(filterType)
      )
    ]
  }
}

export const formatFilterFromConfig = (filterConfiguration: FilterConfigAndAvailableValues) => {
  return {
    filterType: filterConfiguration.filterType,
    filterSubtype: filterConfiguration.filterSubtype,
    operator: filterConfiguration.operators[0],
    valueType: filterConfiguration.valueType,
    values: getDefaultFilterValues(
      filterConfiguration.filterType,
      filterConfiguration.operators[0],
      filterConfiguration.attributeType
    )
  }
}

export const getFilterConfig = (
  filter: { filterType: FilterableEntity; filterSubtype?: string },
  filterConfiguration: FilterConfigAndAvailableValues[]
) => {
  return (
    filterConfiguration.find(
      fc => fc.filterType === filter.filterType && fc.filterSubtype === filter.filterSubtype
    ) || null
  )
}

export const getFirstUnusedFilter = (
  filterGroups: FilterGroup[],
  filterConfiguration: FilterConfigAndAvailableValues[]
) => {
  return filterConfiguration.find(
    config =>
      !filterGroups[filterGroups.length - 1]?.some(
        filter =>
          config.filterType === filter.filterType && config.filterSubtype === filter.filterSubtype
      )
  )
}

export const getActivityFiltersFromQueryString = (): FilterGroup[] | null => {
  try {
    const queryParams = new URLSearchParams(window.location.search.substr(1))
    if (queryParams.has('filters')) {
      const json = atob(queryParams.get('filters') || '')

      const filterGroupsByPage = deserializeFilters(json)

      if (filterGroupsByPage) {
        return filterGroupsByPage.ACTIVITIES
      }
    }
  } catch (e) {
    return null
  }

  return null
}

export const getDefaultFilterValues = (
  filterType: FilterableEntity,
  operator: FilterOperator,
  attributeType?: string
): any => {
  if (
    filterType === FilterableEntity.DATE_RANGE ||
    attributeType === 'DATE' ||
    attributeType === 'DATETIME'
  ) {
    return operator === FilterOperator.BETWEEN ? getDefaultRange() : getDefaultDate()
  }

  return null
}

export const updateFilterType = (
  nextFilter: FilterValues,
  filterGroups: FilterGroup[],
  groupIndex: number,
  filterIndex: number
) => {
  return filterGroups.map((group: any, i: number) =>
    group.map((filter: any, j: number) =>
      i === groupIndex && j === filterIndex ? nextFilter : filter
    )
  )
}

export const updateFilterOperator = (
  operator: FilterOperator,
  filterGroups: FilterGroup[],
  groupIndex: number,
  filterIndex: number
) => {
  return filterGroups.map((group: any, i: number) =>
    group.map((filter: FilterValues, j: number) => ({
      ...filter,
      operator: i === groupIndex && j === filterIndex ? operator : filter.operator,
      values:
        i === groupIndex && j === filterIndex
          ? getDefaultFilterValues(filter.filterType, operator, filter.attributeType) ||
            filter.values
          : filter.values
    }))
  )
}

export const updateFilterValue = (
  nextValues: any,
  filterGroups: FilterGroup[],
  groupIndex: number,
  filterIndex: number
) => {
  return filterGroups.map((group: any, i: number) =>
    group.map((filter: FilterValues, j: number) => ({
      ...filter,
      values: i === groupIndex && j === filterIndex ? nextValues : filter.values
    }))
  )
}

export const addFilter = (
  filter: FilterValues,
  filterGroups: FilterGroup[],
  groupIndex?: number
) => {
  const index = groupIndex ?? filterGroups.length - 1

  return filterGroups.map((group: FilterGroup, i: number) =>
    i === index ? [...group, filter] : group
  )
}

export const removeFilter = (
  filterGroups: FilterGroup[],
  groupIndex: number,
  filterIndex: number
) => {
  return filterGroups
    .map((group: FilterGroup, i: number) =>
      group.filter((_: any, j: number) => i !== groupIndex || j !== filterIndex)
    )
    .filter(group => group.length > 0)
}

export const addFilterGroup = (defaultFilter: FilterValues, filterGroups: FilterGroup[]) => {
  return [...filterGroups, [defaultFilter]]
}
